import {
  Checkbox,
  Divider,
  FormControlLabel,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { RoadSegmentDto } from '@yard/features/road-network/models/road-network.model'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { RoadNetworkViewStore } from '../../stores/nodes/road-network.view-store'

interface Props {
  viewStore: RoadNetworkViewStore
}

export const RoadSegmentContextMenu = observer(({ viewStore }: Props) => {
  const { isContextMenuOpen, contextMenuTargetId, contextMenuTargetType, contextMenuPosition } =
    viewStore

  const open =
    isContextMenuOpen && contextMenuTargetType === 'segment' && contextMenuTargetId !== null

  const segment: RoadSegmentDto | undefined = viewStore.segments.find(
    s => s.id === contextMenuTargetId,
  )

  const [weightLimitInput, setWeightLimitInput] = useState<string>('')

  if (!segment) return null

  const handleDirectionToggle = async () => {
    const next = segment.direction === 'TwoWay' ? 'OneWayForward' : 'TwoWay'
    await viewStore.updateSegmentDirection(segment.id, next)
  }

  const handleOneWayDirectionChange = async (
    _: React.MouseEvent<HTMLElement>,
    value: 'OneWayForward' | 'OneWayReverse' | null,
  ) => {
    if (!value) return
    await viewStore.updateSegmentDirection(segment.id, value)
  }

  const handleBlockedToggle = async () => {
    await viewStore.updateSegmentBlocked(segment.id, !segment.isBlocked)
  }

  const handleWeightLimitBlur = async () => {
    const parsed = weightLimitInput === '' ? undefined : parseFloat(weightLimitInput)
    if (weightLimitInput !== '' && isNaN(parsed!)) return
    await viewStore.updateSegmentWeightLimit(segment.id, parsed)
  }

  const handleDelete = async () => {
    await viewStore.deleteSegment(segment.id)
  }

  const handleOpen = () => {
    setWeightLimitInput(segment.weightLimit != null ? String(segment.weightLimit) : '')
  }

  return (
    <Menu
      open={open}
      onClose={viewStore.closeContextMenu}
      anchorReference='anchorPosition'
      anchorPosition={
        contextMenuPosition
          ? { top: contextMenuPosition.y, left: contextMenuPosition.x }
          : undefined
      }
      slotProps={{ transition: { onEnter: handleOpen } }}
    >
      <MenuItem onClick={handleDirectionToggle}>
        <ListItemText primary={segment.direction === 'TwoWay' ? 'Set one-way' : 'Set two-way'} />
      </MenuItem>
      {segment.direction !== 'TwoWay' && (
        <MenuItem disableRipple>
          <ToggleButtonGroup
            exclusive
            size='small'
            value={segment.direction}
            onChange={handleOneWayDirectionChange}
          >
            <ToggleButton value='OneWayForward'>Forward</ToggleButton>
            <ToggleButton value='OneWayReverse'>Backward</ToggleButton>
          </ToggleButtonGroup>
        </MenuItem>
      )}
      <MenuItem disableRipple>
        <FormControlLabel
          control={<Checkbox checked={segment.isBlocked} onChange={handleBlockedToggle} />}
          label='Blocked'
        />
      </MenuItem>
      <MenuItem disableRipple>
        <TextField
          label='Weight limit (t)'
          size='small'
          type='number'
          value={weightLimitInput}
          onChange={e => setWeightLimitInput(e.target.value)}
          onBlur={handleWeightLimitBlur}
          sx={{ width: 140 }}
        />
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
        <ListItemText primary='Delete segment' />
      </MenuItem>
    </Menu>
  )
})
